<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import evolutionApiClient from 'dashboard/api/channel/evolutionApi';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const qrCodeBase64 = ref(null);
const isLoading = ref(false);
const isLoadingState = ref(false);
const isRestarting = ref(false);
const isForcingNewQr = ref(false);
const errorMessage = ref(null);
const connectionState = ref(null);
const qrCodeDialogRef = ref(null);
const forceNewQrDialogRef = ref(null);

const isConnected = computed(() => connectionState.value === 'open');
const shouldShowQr = computed(() => !isConnected.value);
const isActionDisabled = computed(
  () =>
    isLoading.value ||
    isLoadingState.value ||
    isRestarting.value ||
    isForcingNewQr.value
);
const normalizedState = computed(() => {
  if (isLoadingState.value || isRestarting.value || isForcingNewQr.value) {
    return 'checking';
  }
  if (isConnected.value) return 'connected';
  if (errorMessage.value) return 'error';
  if (
    ['connecting', 'qrcode', 'qr', 'pairing'].includes(connectionState.value)
  ) {
    return 'pending_qr';
  }
  if (connectionState.value) return 'disconnected';
  return 'unknown';
});
const statusClasses = computed(() => {
  const stateClasses = {
    connected:
      'bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-500/20 dark:text-emerald-200 dark:border-emerald-400/60',
    checking:
      'bg-sky-50 text-sky-700 border-sky-300 dark:bg-sky-500/20 dark:text-sky-200 dark:border-sky-400/60',
    pending_qr:
      'bg-blue-50 text-blue-700 border-blue-300 dark:bg-blue-500/20 dark:text-blue-200 dark:border-blue-400/60',
    disconnected:
      'bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-500/20 dark:text-amber-200 dark:border-amber-400/60',
    error:
      'bg-red-50 text-red-700 border-red-300 dark:bg-red-500/20 dark:text-red-200 dark:border-red-400/60',
    unknown:
      'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',
  };

  return stateClasses[normalizedState.value];
});
const statusDotClasses = computed(() => {
  const dotClasses = {
    connected: 'bg-emerald-400 shadow-[0_0_0_3px_rgba(16,185,129,0.2)]',
    checking: 'bg-sky-500',
    pending_qr: 'bg-blue-500',
    disconnected: 'bg-amber-500',
    error: 'bg-red-500',
    unknown: 'bg-slate-400',
  };

  return dotClasses[normalizedState.value];
});
const connectionCardClasses = computed(() => {
  const cardClasses = {
    connected:
      'border-emerald-300 bg-emerald-50/70 dark:border-emerald-400/50 dark:bg-emerald-500/10',
    disconnected:
      'border-amber-300 bg-amber-50/70 dark:border-amber-400/50 dark:bg-amber-500/10',
    pending_qr:
      'border-blue-300 bg-blue-50/70 dark:border-blue-400/50 dark:bg-blue-500/10',
    error:
      'border-red-300 bg-red-50/70 dark:border-red-400/50 dark:bg-red-500/10',
    checking:
      'border-sky-300 bg-sky-50/70 dark:border-sky-400/50 dark:bg-sky-500/10',
    unknown:
      'border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900',
  };

  return cardClasses[normalizedState.value];
});
const connectedPanelClasses = computed(() => {
  const panelClasses = {
    connected:
      'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-400/60 dark:bg-emerald-500/15 dark:text-emerald-100',
    disconnected:
      'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200',
    pending_qr:
      'border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-200',
    error:
      'border-red-200 bg-red-50 text-red-800 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-200',
    checking:
      'border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-200',
    unknown:
      'border-slate-200 bg-slate-50 text-slate-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200',
  };

  return panelClasses[normalizedState.value];
});
const connectedPanelTextClasses = computed(() => {
  if (normalizedState.value === 'connected') {
    return 'text-emerald-700 dark:text-emerald-200';
  }
  if (normalizedState.value === 'error') {
    return 'text-red-700 dark:text-red-300';
  }

  return 'text-slate-700 dark:text-slate-300';
});
const qrCodeImageSrc = computed(() => {
  if (!qrCodeBase64.value) return null;
  if (qrCodeBase64.value.startsWith('data:image')) return qrCodeBase64.value;

  return `data:image/png;base64,${qrCodeBase64.value}`;
});

const openQrCodeDialog = () => {
  if (!qrCodeImageSrc.value) return;
  qrCodeDialogRef.value?.open();
};

const statusText = computed(() => {
  if (isLoadingState.value || isRestarting.value || isForcingNewQr.value) {
    return t(
      'INBOX_MGMT.ADD.EVOLUTION_API_SETTINGS.CONNECTION_STATUS_CHECKING'
    );
  }
  if (connectionState.value === 'open') {
    return t(
      'INBOX_MGMT.ADD.EVOLUTION_API_SETTINGS.CONNECTION_STATUS_CONNECTED'
    );
  }
  if (connectionState.value) {
    return t(
      'INBOX_MGMT.ADD.EVOLUTION_API_SETTINGS.CONNECTION_STATUS_DISCONNECTED'
    );
  }
  return t('INBOX_MGMT.ADD.EVOLUTION_API_SETTINGS.CONNECTION_STATUS_UNKNOWN');
});

const loadConnectionState = async ({ showToast = false } = {}) => {
  isLoadingState.value = true;

  try {
    const response = await evolutionApiClient.getConnectionState(
      props.inbox.id
    );
    connectionState.value =
      response.data?.instance?.state ?? response.data?.state ?? null;
    if (showToast) {
      useAlert(
        t('INBOX_MGMT.ADD.EVOLUTION_API_SETTINGS.CONNECTION_VALIDATE_SUCCESS'),
        { variant: 'positive' }
      );
    }
  } catch {
    connectionState.value = null;
    if (showToast) {
      useAlert(
        t('INBOX_MGMT.ADD.EVOLUTION_API_SETTINGS.CONNECTION_VALIDATE_ERROR')
      );
    }
  } finally {
    isLoadingState.value = false;
  }
};

const loadQrCode = async () => {
  if (!shouldShowQr.value) {
    qrCodeBase64.value = null;
    return;
  }

  isLoading.value = true;
  errorMessage.value = null;
  qrCodeBase64.value = null;

  try {
    const response = await evolutionApiClient.getQrCode(props.inbox.id);
    qrCodeBase64.value =
      response.data?.base64 ??
      response.data?.qrcode ??
      response.data?.code ??
      null;
  } catch {
    errorMessage.value = t(
      'INBOX_MGMT.ADD.EVOLUTION_API_SETTINGS.QR_CODE_ERROR'
    );
  } finally {
    isLoading.value = false;
  }
};

const validateConnection = async () => {
  await loadConnectionState({ showToast: true });
  if (shouldShowQr.value) {
    await loadQrCode();
  }
};

const restartConnection = async () => {
  isRestarting.value = true;
  try {
    await evolutionApiClient.restartConnection(props.inbox.id);
    useAlert(
      t('INBOX_MGMT.ADD.EVOLUTION_API_SETTINGS.CONNECTION_RESTART_SUCCESS'),
      { variant: 'positive' }
    );
  } catch {
    useAlert(
      t('INBOX_MGMT.ADD.EVOLUTION_API_SETTINGS.CONNECTION_RESTART_ERROR')
    );
  } finally {
    isRestarting.value = false;
  }

  await loadConnectionState();
  if (shouldShowQr.value) {
    await loadQrCode();
  }
};

const openForceNewQrDialog = () => {
  forceNewQrDialogRef.value?.open();
};

const forceNewQrCode = async () => {
  isForcingNewQr.value = true;
  errorMessage.value = null;
  qrCodeBase64.value = null;

  try {
    const response = await evolutionApiClient.forceNewQrCode(props.inbox.id);
    qrCodeBase64.value =
      response.data?.base64 ??
      response.data?.qrcode ??
      response.data?.code ??
      null;
    connectionState.value =
      response.data?.instance?.state ?? response.data?.state ?? 'connecting';
    forceNewQrDialogRef.value?.close();
    useAlert(t('INBOX_MGMT.ADD.EVOLUTION_API_SETTINGS.FORCE_NEW_QR_SUCCESS'), {
      variant: 'positive',
    });

    if (!qrCodeBase64.value) {
      await loadQrCode();
    }
  } catch {
    errorMessage.value = t(
      'INBOX_MGMT.ADD.EVOLUTION_API_SETTINGS.FORCE_NEW_QR_ERROR'
    );
    useAlert(errorMessage.value);
  } finally {
    isForcingNewQr.value = false;
  }
};

onMounted(async () => {
  await loadConnectionState();
  if (shouldShowQr.value) {
    await loadQrCode();
  }
});
</script>

<template>
  <div class="mx-8 max-w-5xl">
    <div class="mt-6 mb-5">
      <h2 class="text-lg font-medium text-slate-800 dark:text-slate-100">
        {{ $t('INBOX_MGMT.ADD.EVOLUTION_API_SETTINGS.QR_CODE_TITLE') }}
      </h2>
      <p class="mt-1 mb-0 text-sm text-slate-500 dark:text-slate-400">
        {{ $t('INBOX_MGMT.ADD.EVOLUTION_API_SETTINGS.QR_CODE_SUBTITLE') }}
      </p>
    </div>

    <div
      class="rounded-lg border p-4 transition-colors"
      :class="connectionCardClasses"
    >
      <div
        class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between"
      >
        <div class="flex flex-col gap-2">
          <span class="text-sm font-medium text-slate-700 dark:text-slate-200">
            {{
              $t(
                'INBOX_MGMT.ADD.EVOLUTION_API_SETTINGS.CONNECTION_STATUS_LABEL'
              )
            }}
          </span>
          <span
            class="inline-flex w-fit items-center gap-2 rounded-full border px-3 py-1 text-sm font-medium"
            :class="statusClasses"
          >
            <span class="size-2 rounded-full" :class="statusDotClasses" />
            {{ statusText }}
          </span>
        </div>

        <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
          <button
            type="button"
            class="inline-flex items-center justify-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:text-slate-100 dark:hover:bg-slate-800"
            :disabled="isActionDisabled"
            @click="validateConnection"
          >
            <span class="i-lucide-refresh-cw size-4" />
            {{
              $t(
                'INBOX_MGMT.ADD.EVOLUTION_API_SETTINGS.CONNECTION_VALIDATE_BUTTON'
              )
            }}
          </button>
          <button
            type="button"
            class="inline-flex items-center justify-center gap-2 rounded-md bg-woot-500 px-3 py-2 text-sm font-medium text-white hover:bg-woot-600 disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="isActionDisabled"
            @click="restartConnection"
          >
            <span class="i-lucide-rotate-ccw size-4" />
            {{
              $t(
                'INBOX_MGMT.ADD.EVOLUTION_API_SETTINGS.CONNECTION_RESTART_BUTTON'
              )
            }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="isConnected"
      class="mt-4 rounded-lg border p-4"
      :class="connectedPanelClasses"
    >
      <h3 class="mb-1 text-sm font-semibold">
        {{
          $t('INBOX_MGMT.ADD.EVOLUTION_API_SETTINGS.QR_CODE_CONNECTED_TITLE')
        }}
      </h3>
      <p class="mb-0 text-sm" :class="connectedPanelTextClasses">
        {{
          $t('INBOX_MGMT.ADD.EVOLUTION_API_SETTINGS.QR_CODE_CONNECTED_SUBTITLE')
        }}
      </p>
    </div>

    <div
      v-else
      class="mt-4 grid gap-5 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900 lg:grid-cols-[minmax(280px,360px)_1fr]"
    >
      <div class="flex flex-col items-center gap-3">
        <div
          v-if="isLoading"
          class="flex aspect-square w-full max-w-80 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800"
        >
          <span class="text-sm text-slate-500 dark:text-slate-400">
            {{ $t('INBOX_MGMT.ADD.EVOLUTION_API_SETTINGS.QR_CODE_LOADING') }}
          </span>
        </div>

        <div
          v-else-if="errorMessage"
          class="w-full rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300"
        >
          {{ errorMessage }}
        </div>

        <button
          v-else-if="qrCodeImageSrc"
          type="button"
          class="group relative aspect-square w-full max-w-80 rounded-lg border border-slate-200 bg-white p-4 shadow-sm transition hover:border-woot-400 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-woot-500 dark:border-slate-700"
          :aria-label="$t('INBOX_MGMT.ADD.EVOLUTION_API_SETTINGS.QR_CODE_OPEN')"
          @click="openQrCodeDialog"
        >
          <img
            :src="qrCodeImageSrc"
            :alt="$t('INBOX_MGMT.ADD.EVOLUTION_API_SETTINGS.QR_CODE_TITLE')"
            class="h-full w-full object-contain"
          />
          <span
            class="absolute inset-x-4 bottom-4 inline-flex items-center justify-center gap-2 rounded-md bg-slate-950/80 px-3 py-2 text-sm font-medium text-white opacity-0 transition group-hover:opacity-100 group-focus:opacity-100"
          >
            <span class="i-lucide-maximize-2 size-4" />
            {{ $t('INBOX_MGMT.ADD.EVOLUTION_API_SETTINGS.QR_CODE_OPEN') }}
          </span>
        </button>

        <div
          v-else
          class="w-full rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
        >
          {{ $t('INBOX_MGMT.ADD.EVOLUTION_API_SETTINGS.QR_CODE_EMPTY') }}
        </div>
      </div>

      <div class="flex flex-col justify-between gap-5">
        <div>
          <h3
            class="mb-2 text-sm font-semibold text-slate-800 dark:text-slate-100"
          >
            {{ $t('INBOX_MGMT.ADD.EVOLUTION_API_SETTINGS.QR_CODE_HELP_TITLE') }}
          </h3>
          <ol
            class="mb-0 list-decimal space-y-2 pl-5 text-sm text-slate-600 marker:font-medium marker:text-slate-900 dark:text-slate-300 dark:marker:text-slate-100"
          >
            <li>
              {{
                $t('INBOX_MGMT.ADD.EVOLUTION_API_SETTINGS.QR_CODE_STEP_OPEN')
              }}
            </li>
            <li>
              {{
                $t('INBOX_MGMT.ADD.EVOLUTION_API_SETTINGS.QR_CODE_STEP_SCAN')
              }}
            </li>
            <li>
              {{
                $t('INBOX_MGMT.ADD.EVOLUTION_API_SETTINGS.QR_CODE_STEP_WAIT')
              }}
            </li>
          </ol>
        </div>

        <div class="flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            class="inline-flex items-center justify-center gap-2 rounded-md bg-woot-500 px-3 py-2 text-sm font-medium text-white hover:bg-woot-600 disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="isActionDisabled"
            @click="loadQrCode"
          >
            <span class="i-lucide-qr-code size-4" />
            {{ $t('INBOX_MGMT.ADD.EVOLUTION_API_SETTINGS.QR_CODE_REFRESH') }}
          </button>
          <button
            v-if="qrCodeImageSrc"
            type="button"
            class="inline-flex items-center justify-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-100 dark:hover:bg-slate-800"
            @click="openQrCodeDialog"
          >
            <span class="i-lucide-maximize-2 size-4" />
            {{ $t('INBOX_MGMT.ADD.EVOLUTION_API_SETTINGS.QR_CODE_OPEN') }}
          </button>
        </div>
      </div>
    </div>

    <div
      class="mt-5 flex flex-col gap-4 rounded-lg border border-red-200 bg-red-50/70 p-4 dark:border-red-500/30 dark:bg-red-500/10 sm:flex-row sm:items-center sm:justify-between"
    >
      <div class="min-w-0">
        <div class="flex items-center gap-2">
          <span
            class="i-lucide-triangle-alert size-4 shrink-0 text-red-600 dark:text-red-300"
          />
          <h3 class="mb-0 text-sm font-semibold text-red-800 dark:text-red-200">
            {{ $t('INBOX_MGMT.ADD.EVOLUTION_API_SETTINGS.FORCE_NEW_QR_TITLE') }}
          </h3>
        </div>
        <p class="mt-1 mb-0 text-sm text-red-700 dark:text-red-300">
          {{
            $t('INBOX_MGMT.ADD.EVOLUTION_API_SETTINGS.FORCE_NEW_QR_DESCRIPTION')
          }}
        </p>
      </div>
      <button
        type="button"
        class="inline-flex shrink-0 items-center justify-center gap-2 rounded-md border border-red-300 bg-white px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-500/50 dark:bg-transparent dark:text-red-200 dark:hover:bg-red-500/20"
        :disabled="isActionDisabled"
        @click="openForceNewQrDialog"
      >
        <span class="i-lucide-unplug size-4" />
        {{ $t('INBOX_MGMT.ADD.EVOLUTION_API_SETTINGS.FORCE_NEW_QR_BUTTON') }}
      </button>
    </div>

    <Dialog
      ref="qrCodeDialogRef"
      width="xl"
      :title="$t('INBOX_MGMT.ADD.EVOLUTION_API_SETTINGS.QR_CODE_MODAL_TITLE')"
      :description="
        $t('INBOX_MGMT.ADD.EVOLUTION_API_SETTINGS.QR_CODE_MODAL_DESCRIPTION')
      "
      :show-confirm-button="false"
      :show-cancel-button="false"
    >
      <div class="flex flex-col items-center gap-4">
        <img
          v-if="qrCodeImageSrc"
          :src="qrCodeImageSrc"
          :alt="$t('INBOX_MGMT.ADD.EVOLUTION_API_SETTINGS.QR_CODE_TITLE')"
          class="aspect-square w-full max-w-[480px] rounded-lg bg-white p-5"
        />
        <button
          type="button"
          class="inline-flex items-center justify-center rounded-md border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-100 dark:hover:bg-slate-800"
          @click="qrCodeDialogRef?.close()"
        >
          {{ $t('DIALOG.BUTTONS.CANCEL') }}
        </button>
      </div>
    </Dialog>

    <Dialog
      ref="forceNewQrDialogRef"
      type="alert"
      width="md"
      :title="
        $t('INBOX_MGMT.ADD.EVOLUTION_API_SETTINGS.FORCE_NEW_QR_CONFIRM_TITLE')
      "
      :description="
        $t(
          'INBOX_MGMT.ADD.EVOLUTION_API_SETTINGS.FORCE_NEW_QR_CONFIRM_DESCRIPTION'
        )
      "
      :confirm-button-label="
        $t('INBOX_MGMT.ADD.EVOLUTION_API_SETTINGS.FORCE_NEW_QR_CONFIRM_BUTTON')
      "
      :is-loading="isForcingNewQr"
      :disable-confirm-button="isForcingNewQr"
      @confirm="forceNewQrCode"
    />
  </div>
</template>
